/**
 * GenericMultiFileRow Component
 * 
 * File row component for file types that support multiple uploads (e.g., BANK_TX, TAX_ACCOUNT).
 * Displays all uploaded files for a category and allows adding more without replacing existing ones.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  Loader2, 
  Files,
  Info,
  Trash2,
  Download,
  Plus,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Settings2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getFileKeyConfig, getFileKeyDisplayName } from '@/lib/modules/file-keys';
import { formatWithUTC } from '@/lib/datetime';
import { FieldMappingPrecheckModal } from '@/components/FieldMappingPrecheckModal';
import { useFieldMappings } from '@/hooks/useFieldMappings';
import { useDocumentParser } from '@/hooks/useDocumentParser';
import type { FileKey } from '@/lib/modules/types';

interface UploadedFile {
  id: string;
  filename: string;
  original_filename: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

interface GenericMultiFileRowProps {
  fileKey: FileKey;
  required: boolean;
  auditId: string;
  moduleId: string;
  onUploadComplete: () => void;
}

// Helper to format file size in human-readable format
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function GenericMultiFileRow({ 
  fileKey, 
  required, 
  auditId, 
  moduleId, 
  onUploadComplete,
}: GenericMultiFileRowProps) {
  const { toast } = useToast();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showFilesList, setShowFilesList] = useState(false);
  
  // Field mapping modal state
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [pendingCsvContent, setPendingCsvContent] = useState<string>('');
  const [parsedSourceType, setParsedSourceType] = useState<'pdf' | 'csv'>('csv');
  const [extractedRowCount, setExtractedRowCount] = useState<number | undefined>(undefined);
  const { saveMapping, getMapping } = useFieldMappings();
  const [hasSavedMapping, setHasSavedMapping] = useState(false);
  
  // PDF parsing hook
  const { parseDocument, isParsing, progress: parseProgress, toCsvContent } = useDocumentParser({
    moduleType: moduleId,
  });
  
  const config = getFileKeyConfig(fileKey);
  const displayName = getFileKeyDisplayName(fileKey);

  // Fetch existing files for this file_key (exclude soft-deleted)
  const fetchFiles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('audit_documents')
        .select('id, filename, original_filename, storage_path, mime_type, size_bytes, created_at')
        .eq('audit_id', auditId)
        .eq('file_key', fileKey)
        .is('deleted_at', null) // Exclude soft-deleted files
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUploadedFiles(data || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setIsLoading(false);
    }
  }, [auditId, fileKey]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);
  
  // Check if mapping exists
  const checkExistingMapping = useCallback(async () => {
    try {
      const mapping = await getMapping(auditId, moduleId);
      setHasSavedMapping(!!mapping);
    } catch {
      setHasSavedMapping(false);
    }
  }, [auditId, moduleId, getMapping]);
  
  useEffect(() => {
    checkExistingMapping();
  }, [checkExistingMapping]);

  // Handle file upload
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setIsUploading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Inte inloggad');

      for (const file of acceptedFiles) {
        // Sanitize filename
        const originalFileName = file.name;
        const sanitizedFileName = originalFileName
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[:\/<>|"\\?*]/g, '_')
          .replace(/\s+/g, '_')
          .replace(/[^\w.-]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');

        // Upload to storage (don't delete existing files!)
        const filePath = `${auditId}/${moduleId}/${fileKey}/${Date.now()}_${sanitizedFileName}`;
        const { error: uploadError } = await supabase.storage
          .from('audit-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Save document record
        const { error: dbError } = await supabase
          .from('audit_documents')
          .insert({
            audit_id: auditId,
            filename: sanitizedFileName,
            original_filename: originalFileName,
            mime_type: file.type,
            size_bytes: file.size,
            storage_path: filePath,
            uploaded_by: user.id,
            module_type: moduleId,
            file_key: fileKey,
          });

        if (dbError) throw dbError;
      }

      toast({ 
        title: 'Filer uppladdade', 
        description: `${acceptedFiles.length} fil(er) uppladdade` 
      });
      
      // Refresh file list
      await fetchFiles();
      onUploadComplete();
      
      // For the first file, offer field mapping
      if (uploadedFiles.length === 0 && acceptedFiles.length > 0) {
        const firstFile = acceptedFiles[0];
        const isCsvFile = firstFile.type === 'text/csv' || firstFile.name.toLowerCase().endsWith('.csv');
        const isPdfFile = firstFile.type === 'application/pdf' || firstFile.name.toLowerCase().endsWith('.pdf');
        const isExcelFile = firstFile.type.includes('spreadsheet') || 
          firstFile.name.toLowerCase().endsWith('.xlsx') || 
          firstFile.name.toLowerCase().endsWith('.xls');
        
        if (isCsvFile) {
          const csvContent = await firstFile.text();
          setPendingCsvContent(csvContent);
          setParsedSourceType('csv');
          setExtractedRowCount(undefined);
          setShowMappingModal(true);
        } else if (isPdfFile || isExcelFile) {
          toast({
            title: 'Tolkar fil...',
            description: 'AI extraherar tabelldata',
          });
          
          const parsed = await parseDocument(firstFile, moduleId);
          
          if (parsed && parsed.rows.length > 0) {
            const csvContent = toCsvContent(parsed);
            setPendingCsvContent(csvContent);
            setParsedSourceType(isPdfFile ? 'pdf' : 'csv');
            setExtractedRowCount(parsed.rows.length);
            setShowMappingModal(true);
          }
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({ 
        title: 'Uppladdning misslyckades', 
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive' 
      });
    } finally {
      setIsUploading(false);
    }
  }, [auditId, moduleId, fileKey, toast, fetchFiles, onUploadComplete, uploadedFiles.length, parseDocument, toCsvContent]);

  // Handle file deletion (soft delete - recoverable for 30 days)
  const handleDelete = useCallback(async (fileId: string, _storagePath: string) => {
    try {
      setDeletingId(fileId);
      
      // Get current user for soft delete tracking
      const { data: { user } } = await supabase.auth.getUser();
      
      // Soft delete: Mark as deleted instead of removing
      // File stays in storage and can be recovered for 30 days
      const { error: dbError } = await supabase
        .from('audit_documents')
        .update({ 
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null
        })
        .eq('id', fileId);

      if (dbError) throw dbError;

      toast({ 
        title: 'Fil raderad',
        description: 'Filen kan återställas i 30 dagar'
      });
      await fetchFiles();
      onUploadComplete();
    } catch (error) {
      console.error('Delete error:', error);
      toast({ 
        title: 'Radering misslyckades', 
        variant: 'destructive' 
      });
    } finally {
      setDeletingId(null);
    }
  }, [toast, fetchFiles, onUploadComplete]);

  // Handle download
  const handleDownload = useCallback(async (storagePath: string, filename: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('audit-documents')
        .download(storagePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast({ 
        title: 'Nedladdning misslyckades', 
        variant: 'destructive' 
      });
    }
  }, [toast]);
  
  // Handler when field mapping is confirmed
  const handleMappingConfirm = useCallback(async (
    mapping: Record<string, string>, 
    dialect: { delimiter: string; hasHeader: boolean }
  ) => {
    try {
      await saveMapping(
        auditId,
        moduleId,
        mapping,
        { delimiter: dialect.delimiter, encoding: 'utf-8', hasHeader: dialect.hasHeader }
      );
      
      setShowMappingModal(false);
      setPendingCsvContent('');
      setHasSavedMapping(true);
      
      toast({
        title: 'Fältmappning sparad',
        description: 'Mappningen kommer användas vid analys',
      });
    } catch (error) {
      console.error('Failed to save mapping:', error);
      toast({
        title: 'Kunde inte spara mappning',
        variant: 'destructive',
      });
    }
  }, [auditId, moduleId, saveMapping, toast]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: true,
    disabled: isUploading || isParsing,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="border rounded-lg p-4 bg-card animate-pulse">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Laddar filer...</span>
        </div>
      </div>
    );
  }

  const hasFiles = uploadedFiles.length > 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="border rounded-lg p-4 bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${hasFiles ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
              {hasFiles ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{displayName}</span>
                {required && <Badge variant="default" className="text-xs">Obligatorisk</Badge>}
                <Badge variant="secondary" className="text-xs">
                  <Files className="h-3 w-3 mr-1" />
                  Multi-fil
                </Badge>
                {hasSavedMapping && (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                    <Settings2 className="h-3 w-3 mr-1" />
                    Mappning sparad
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {hasFiles 
                  ? `${uploadedFiles.length} fil(er) uppladdade`
                  : config?.multipleHint 
                    ? 'Ladda upp en eller flera filer' 
                    : 'Ingen fil uppladdad'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasFiles && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilesList(!showFilesList)}
              >
                {showFilesList ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                {showFilesList ? 'Dölj' : 'Visa filer'}
              </Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Button
                      variant={hasFiles ? "outline" : "default"}
                      size="sm"
                      disabled={isUploading || isParsing}
                    >
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      {hasFiles ? 'Lägg till fler' : 'Ladda upp'}
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Format: {config?.formats.join(', ')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        
        {/* Drag indicator */}
        {isDragActive && (
          <div className="mt-3 border-2 border-dashed border-primary rounded-lg p-4 text-center bg-primary/5">
            <Upload className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="text-sm text-primary">Släpp filerna här</p>
          </div>
        )}
      </div>

      {/* Files list */}
      {showFilesList && hasFiles && (
        <div className="space-y-2 pl-4">
          {uploadedFiles.map((file) => (
            <div 
              key={file.id} 
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{file.original_filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.size_bytes)} • {formatWithUTC(file.created_at)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleDownload(file.storage_path, file.original_filename)}
                >
                  <Download className="h-4 w-4" />
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={deletingId === file.id}
                    >
                      {deletingId === file.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Radera fil</AlertDialogTitle>
                      <AlertDialogDescription>
                        Är du säker på att du vill radera "{file.original_filename}"?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Avbryt</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDelete(file.id, file.storage_path)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Radera
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info about multi-file support */}
      {!hasFiles && (
        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
            Du kan ladda upp flera filer för denna kategori (t.ex. ett utdrag per bankkonto eller period).
          </AlertDescription>
        </Alert>
      )}
      
      {/* Field Mapping Modal */}
      <FieldMappingPrecheckModal
        isOpen={showMappingModal}
        onClose={() => {
          setShowMappingModal(false);
          setPendingCsvContent('');
        }}
        csvContent={pendingCsvContent}
        onConfirm={handleMappingConfirm}
        customerId={auditId}
        moduleName={moduleId}
        sourceType={parsedSourceType}
        extractedRowCount={extractedRowCount}
      />
      
      {/* PDF Parsing Loading Dialog */}
      <Dialog open={isParsing}>
        <DialogContent className="text-center sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center gap-4 py-4">
            <FileSpreadsheet className="h-12 w-12 text-primary animate-pulse" />
            <DialogTitle>Tolkar fil...</DialogTitle>
            <DialogDescription>
              AI extraherar tabelldata. Detta kan ta 10-30 sekunder.
            </DialogDescription>
            <Progress value={parseProgress} className="w-full" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
